/*
 * ------------------------------------------------------------
 * Barra fixa de adicionar ao carrinho, no celular e no desktop.
 *
 * Quatro responsabilidades, nenhuma delas é adicionar ao carrinho:
 *   1. Mostrar a barra só enquanto o botão original está fora da viewport
 *      (IntersectionObserver sobre ele).
 *   2. Espelhar o estado do botão original — texto, disabled e a classe
 *      loading — porque o product-form.js só conhece o botão que está dentro
 *      dele. Ver o cabeçalho de product-sticky-atc.liquid.
 *   3. Espelhar o preço, que o product-info.js troca por innerHTML em
 *      #price-<sectionId> a cada mudança de variante.
 *   4. Trocar a miniatura (visível só no desktop) quando a variante escolhida
 *      tem imagem própria, lendo o mapa que o snippet imprime em
 *      data-variant-images. A galeria deste tema é um Swiper com autoplay que
 *      ignora a variante, então não dá para espelhar dela.
 *
 * Por que MutationObserver e não pubsub: na troca de variante o product-info.js
 * não recria o botão, ele muta o que já existe (toggleSubmitButton). Observar o
 * DOM cobre esse caminho e também o toggleSubmitButton disparado pelo próprio
 * product-form.js durante o add-to-cart, sem depender da ordem dos subscribers.
 * ------------------------------------------------------------
 */

class ProductStickyAtc extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector('[data-sticky-submit]');
    this.buttonText = this.querySelector('[data-sticky-submit-text]');
    this.priceNode = this.querySelector('[data-sticky-price]');
    this.image = this.querySelector('[data-sticky-image]');
    this.formId = this.button ? this.button.getAttribute('form') : null;

    /* Atributo malformado não pode derrubar a barra inteira: sem mapa, a
       miniatura fica na imagem que o Liquid já renderizou. */
    try {
      this.variantImages = JSON.parse(this.dataset.variantImages || '{}');
    } catch (error) {
      this.variantImages = {};
    }

    this.observer = null;
    this.buttonMutations = null;
    this.priceMutations = null;

    this.attach();

    /* No editor de tema a section é recarregada inteira; religar é mais barato
       do que tentar sobreviver à troca. */
    this.onSectionUpdate = this.attach.bind(this);
    document.addEventListener('shopify:section:load', this.onSectionUpdate);
  }

  disconnectedCallback() {
    this.detach();
    document.removeEventListener('shopify:section:load', this.onSectionUpdate);
  }

  detach() {
    [this.observer, this.buttonMutations, this.priceMutations].forEach((observer) => {
      if (observer) observer.disconnect();
    });
    this.observer = null;
    this.buttonMutations = null;
    this.priceMutations = null;
  }

  attach() {
    this.detach();

    this.target = document.getElementById(this.dataset.targetButton);
    this.priceSource = this.dataset.priceSource ? document.getElementById(this.dataset.priceSource) : null;

    /* Sem botão original não há o que espelhar nem o que observar: a barra
       fica escondida em vez de virar um botão órfão. */
    if (!this.target) {
      this.hidden = true;
      return;
    }

    this.syncState();
    this.syncPrice();
    this.syncImage();

    this.observer = new IntersectionObserver(
      ([entry]) => {
        this.hidden = entry.isIntersecting;
      },
      /* rootMargin negativo embaixo, na altura da própria barra: sem ele o botão
         original conta como visível enquanto ainda está escondido atrás dela, e
         a barra sumiria deixando a pessoa sem botão nenhum. */
      { rootMargin: '0px 0px -80px 0px' }
    );
    this.observer.observe(this.target);

    this.buttonMutations = new MutationObserver(() => this.syncState());
    this.buttonMutations.observe(this.target, {
      attributes: true,
      attributeFilter: ['disabled', 'aria-disabled', 'class'],
      childList: true,
      characterData: true,
      subtree: true,
    });

    if (this.priceSource) {
      /* A troca de variante reescreve o preço; é o gatilho mais confiável para
         reavaliar também a imagem, que não tem nó próprio para observar. */
      this.priceMutations = new MutationObserver(() => {
        this.syncPrice();
        this.syncImage();
      });
      this.priceMutations.observe(this.priceSource, { childList: true, subtree: true, characterData: true });
    }
  }

  syncState() {
    if (!this.target || !this.button) return;

    const isDisabled = this.target.disabled || this.target.getAttribute('aria-disabled') === 'true';
    this.button.disabled = isDisabled;
    this.button.classList.toggle('loading', this.target.classList.contains('loading'));

    /* O texto do botão original já reflete esgotado / indisponível / variante
       sem estoque, então copiar é mais confiável do que recalcular. */
    const targetText = this.target.querySelector('span:not(.loading__spinner):not(.visually-hidden)');
    const label = (targetText ? targetText.textContent : this.target.textContent).trim();
    if (label && this.buttonText) this.buttonText.textContent = label;
  }

  syncPrice() {
    if (!this.priceSource || !this.priceNode) return;
    this.priceNode.innerHTML = this.priceSource.innerHTML;
  }

  syncImage() {
    if (!this.image || !this.formId) return;

    /* O <form> é reescrito a cada troca de variante, então nada de guardar
       referência: busca na hora. */
    const form = document.getElementById(this.formId);
    const input = form ? form.querySelector('[name="id"]') : null;
    const src = input ? this.variantImages[input.value] : null;

    /* Variante sem imagem própria mantém a que está lá — trocar por vazio seria
       pior do que mostrar a imagem principal do produto. */
    if (src && this.image.getAttribute('src') !== src) this.image.src = src;
  }
}

customElements.define('product-sticky-atc', ProductStickyAtc);
